import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import ReviewForm from '../components/ReviewForm';
import HashtagInput from '../components/HashtagInput';
import DailyCheckIn from '../components/DailyCheckIn';
import DailyQuests from '../components/DailyQuests';
import DeveloperAnalytics from '../components/DeveloperAnalytics';

const Dashboard = () => {
  const { user, isIndieDev } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('community');
  
  // Community Card State
  const [myCards, setMyCards] = useState([]);
  const [showCardForm, setShowCardForm] = useState(false);
  const [cardFormData, setCardFormData] = useState({
    name: '',
    about: '',
    linktree: '',
    socialLinks: [],
    donationLinks: [],
    hashtags: []
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [newSocialLink, setNewSocialLink] = useState({ platform: '', url: '' });
  const [newDonationLink, setNewDonationLink] = useState({ platform: '', url: '' });

  // Indie Games State
  const [myGames, setMyGames] = useState([]);
  const [showGameForm, setShowGameForm] = useState(false);
  const [gameFormData, setGameFormData] = useState({
    name: '',
    description: '',
    price: '',
    storeLinks: [],
    hashtags: [],
    version: '',
    releaseStatus: '',
    maturityRating: '',
    genre: [],
    features: [],
    systemRequirements: {
      minimum: {
        os: '',
        storage: '',
        processor: '',
        memory: '',
        graphics: '',
        directX: ''
      },
      recommended: {
        os: '',
        storage: '',
        processor: '',
        memory: '',
        graphics: '',
        directX: ''
      }
    }
  });
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  const [newStoreLink, setNewStoreLink] = useState({ platform: '', url: '' });
  const [newGenre, setNewGenre] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [editingGameId, setEditingGameId] = useState(null);

  // Reviews State
  const [myReviews, setMyReviews] = useState([]);
  const [editingReviewId, setEditingReviewId] = useState(null);

  // Giveaways State
  const [myGiveaways, setMyGiveaways] = useState([]);

  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  const fetchMyCards = useCallback(async () => {
    try {
      const response = await axios.get('/api/community-cards/my-cards');
      setMyCards(response.data?.data || []);
      setVerificationStatus(response.data?.verificationStatus || null);
      
      // Show verification modal if user has cards but not all accounts are verified
      if (response.data?.data?.length > 0 && response.data?.verificationStatus && !response.data?.verificationStatus?.allVerified) {
        setShowVerificationModal(true);
      }
    } catch (err) {
      console.error('Error fetching cards:', err);
    }
  }, []);

  const fetchMyGames = useCallback(async () => {
    try {
      const response = await axios.get('/api/indie-games/my-games');
      setMyGames(response.data?.data || []);
    } catch (err) {
      console.error('Error fetching games:', err);
    }
  }, []);

  const fetchMyReviews = useCallback(async () => {
    try {
      const response = await axios.get('/api/reviews/user/me');
      setMyReviews(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    }
  }, []);

  const fetchMyGiveaways = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/giveaways?userId=${user._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle both old (array) and new (paginated) response formats
      setMyGiveaways(response.data.giveaways || response.data || []);
    } catch (err) {
      console.error('Error fetching giveaways:', err);
    }
  }, [user._id]);

  useEffect(() => {
    fetchMyCards();
    fetchMyReviews();
    fetchMyGiveaways();
    if (isIndieDev) {
      fetchMyGames();
    }
  }, [isIndieDev, fetchMyCards, fetchMyReviews, fetchMyGiveaways, fetchMyGames]);

  const handleDeleteGiveaway = async (giveawayId) => {
    if (!window.confirm('Are you sure you want to delete this giveaway? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `/api/giveaways/${giveawayId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Remove giveaway from list
      setMyGiveaways(myGiveaways.filter(g => g._id !== giveawayId));
      setMessage({ type: 'success', text: 'Giveaway deleted successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to delete giveaway' });
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `/api/reviews/${reviewId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Remove review from list
      setMyReviews(myReviews.filter(r => r._id !== reviewId));
      setMessage({ type: 'success', text: 'Review deleted successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to delete review' });
    }
  };

  // Community Card Functions
  const handleCardChange = (e) => {
    setCardFormData({
      ...cardFormData,
      [e.target.name]: e.target.value
    });
  };

  const addSocialLink = () => {
    if (newSocialLink.platform && newSocialLink.url) {
      setCardFormData({
        ...cardFormData,
        socialLinks: [...cardFormData.socialLinks, newSocialLink]
      });
      setNewSocialLink({ platform: '', url: '' });
    }
  };

  const removeSocialLink = (index) => {
    setCardFormData({
      ...cardFormData,
      socialLinks: cardFormData.socialLinks.filter((_, i) => i !== index)
    });
  };

  const addDonationLink = () => {
    if (newDonationLink.platform && newDonationLink.url) {
      setCardFormData({
        ...cardFormData,
        donationLinks: [...cardFormData.donationLinks, newDonationLink]
      });
      setNewDonationLink({ platform: '', url: '' });
    }
  };

  const removeDonationLink = (index) => {
    setCardFormData({
      ...cardFormData,
      donationLinks: cardFormData.donationLinks.filter((_, i) => i !== index)
    });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500 * 1024) {
        setMessage({ type: 'error', text: 'Avatar file size must be less than 500KB' });
        return;
      }
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleCardSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Check verification status before creating new card
    if (myCards.length === 0 && verificationStatus && !verificationStatus.allVerified) {
      setMessage({ 
        type: 'error', 
        text: 'Please verify all your social accounts (Steam, Discord, Twitter) before creating a community card.' 
      });
      setShowVerificationModal(true);
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('name', cardFormData.name);
      formData.append('about', cardFormData.about);
      formData.append('linktree', cardFormData.linktree);
      formData.append('socialLinks', JSON.stringify(cardFormData.socialLinks));
      formData.append('donationLinks', JSON.stringify(cardFormData.donationLinks));
      formData.append('hashtags', JSON.stringify(cardFormData.hashtags));
      
      if (avatarFile) {
        formData.append('avatar', avatarFile);
      }

      if (myCards.length > 0) {
        await axios.put(`/api/community-cards/${myCards[0]._id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setMessage({ type: 'success', text: 'Community card updated and sent for approval!' });
      } else {
        await axios.post('/api/community-cards', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setMessage({ type: 'success', text: 'Community card created and sent for approval!' });
      }
      setShowCardForm(false);
      fetchMyCards();
      resetCardForm();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to save card';
      setMessage({ type: 'error', text: errorMsg });
      
      // Show verification modal if it's a verification error
      if (err.response?.data?.unverifiedAccounts) {
        setShowVerificationModal(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetCardForm = () => {
    setCardFormData({
      name: '',
      about: '',
      linktree: '',
      socialLinks: [],
      donationLinks: [],
      hashtags: []
    });
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  const editCard = (card) => {
    setCardFormData({
      name: card.name,
      about: card.about,
      linktree: card.linktree || '',
      socialLinks: card.socialLinks || [],
      donationLinks: card.donationLinks || [],
      hashtags: card.hashtags || []
    });
    // Set avatar preview if it exists (use Cloudinary URL directly)
    if (card.avatar) {
      setAvatarPreview(card.avatar);
    } else {
      setAvatarPreview(null);
    }
    // Clear the file input when editing
    setAvatarFile(null);
    setShowCardForm(true);
  };

  const deleteCard = async (cardId) => {
    if (window.confirm('Are you sure you want to delete this card? This will require admin approval.')) {
      try {
        await axios.delete(`/api/community-cards/${cardId}`);
        setMessage({ type: 'success', text: 'Delete request sent for approval!' });
        fetchMyCards();
      } catch (err) {
        setMessage({ type: 'error', text: 'Failed to delete card' });
      }
    }
  };

  // Indie Game Functions
  const handleGameChange = (e) => {
    setGameFormData({
      ...gameFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleSystemReqChange = (tier, field, value) => {
    setGameFormData({
      ...gameFormData,
      systemRequirements: {
        ...gameFormData.systemRequirements,
        [tier]: {
          ...gameFormData.systemRequirements[tier],
          [field]: value
        }
      }
    });
  };

  const addGenre = () => {
    if (newGenre.trim() && gameFormData.genre.length < 10) {
      setGameFormData({
        ...gameFormData,
        genre: [...gameFormData.genre, newGenre.trim()]
      });
      setNewGenre('');
    }
  };

  const removeGenre = (index) => {
    setGameFormData({
      ...gameFormData,
      genre: gameFormData.genre.filter((_, i) => i !== index)
    });
  };

  const addFeature = () => {
    if (newFeature.trim() && gameFormData.features.length < 20) {
      setGameFormData({
        ...gameFormData,
        features: [...gameFormData.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setGameFormData({
      ...gameFormData,
      features: gameFormData.features.filter((_, i) => i !== index)
    });
  };

  const addStoreLink = () => {
    if (newStoreLink.platform && newStoreLink.url) {
      setGameFormData({
        ...gameFormData,
        storeLinks: [...gameFormData.storeLinks, newStoreLink]
      });
      setNewStoreLink({ platform: '', url: '' });
    }
  };

  const removeStoreLink = (index) => {
    setGameFormData({
      ...gameFormData,
      storeLinks: gameFormData.storeLinks.filter((_, i) => i !== index)
    });
  };

  const handleMediaChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Combine with existing files
    const allFiles = [...mediaFiles, ...files];
    
    if (allFiles.length > 10) {
      setMessage({ type: 'error', text: `You can upload a maximum of 10 media files. You currently have ${mediaFiles.length} file(s). You can add ${10 - mediaFiles.length} more.` });
      e.target.value = ''; // Reset input
      return;
    }

    const validFiles = files.filter(file => {
      const isValid = file.type.startsWith('image/') || file.type.startsWith('video/');
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
      
      if (!isValid) {
        setMessage({ type: 'error', text: `${file.name} is not a valid image or video file` });
        return false;
      }
      if (!isValidSize) {
        setMessage({ type: 'error', text: `${file.name} exceeds 5MB size limit` });
        return false;
      }
      return true;
    });

    // Add to existing files
    const updatedFiles = [...mediaFiles, ...validFiles];
    setMediaFiles(updatedFiles);
    
    // Create previews for all files
    const previews = updatedFiles.map(file => ({
      url: URL.createObjectURL(file),
      type: file.type.startsWith('image/') ? 'image' : 'video'
    }));
    setMediaPreviews(previews);
    
    // Reset input to allow selecting the same file again if needed
    e.target.value = '';
  };

  const removeMediaFile = (index) => {
    const updatedFiles = mediaFiles.filter((_, i) => i !== index);
    const updatedPreviews = mediaPreviews.filter((_, i) => i !== index);
    setMediaFiles(updatedFiles);
    setMediaPreviews(updatedPreviews);
  };

  const handleGameSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('name', gameFormData.name);
      formData.append('description', gameFormData.description);
      formData.append('price', gameFormData.price);
      formData.append('storeLinks', JSON.stringify(gameFormData.storeLinks));
      formData.append('hashtags', JSON.stringify(gameFormData.hashtags));
      formData.append('version', gameFormData.version);
      formData.append('releaseStatus', gameFormData.releaseStatus);
      formData.append('maturityRating', gameFormData.maturityRating);
      formData.append('genre', JSON.stringify(gameFormData.genre));
      formData.append('features', JSON.stringify(gameFormData.features));
      formData.append('systemRequirements', JSON.stringify(gameFormData.systemRequirements));
      
      // Append media files
      mediaFiles.forEach(file => {
        formData.append('media', file);
      });

      if (editingGameId) {
        // Update existing game
        await axios.put(`/api/indie-games/${editingGameId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setMessage({ type: 'success', text: 'Game updated and sent for re-approval!' });
        setEditingGameId(null);
      } else {
        // Create new game
        await axios.post('/api/indie-games', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setMessage({ type: 'success', text: 'Game created and sent for approval!' });
      }
      
      setShowGameForm(false);
      fetchMyGames();
      resetGameForm();
    } catch (err) {
      console.error('Error saving game:', err.response?.data);
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to save game' });
    } finally {
      setLoading(false);
    }
  };

  const resetGameForm = () => {
    setGameFormData({
      name: '',
      description: '',
      price: '',
      storeLinks: [],
      hashtags: [],
      version: '',
      releaseStatus: '',
      maturityRating: '',
      genre: [],
      features: [],
      systemRequirements: {
        minimum: {
          os: '',
          storage: '',
          processor: '',
          memory: '',
          graphics: '',
          directX: ''
        },
        recommended: {
          os: '',
          storage: '',
          processor: '',
          memory: '',
          graphics: '',
          directX: ''
        }
      }
    });
    setMediaFiles([]);
    setMediaPreviews([]);
    setNewGenre('');
    setNewFeature('');
    setEditingGameId(null);
  };

  const handleEditGame = (game) => {
    setGameFormData({
      name: game.name || '',
      description: game.description || '',
      price: game.price || '',
      storeLinks: game.storeLinks || [],
      hashtags: game.hashtags || [],
      version: game.version || '',
      releaseStatus: game.releaseStatus || '',
      maturityRating: game.maturityRating || '',
      genre: game.genre || [],
      features: game.features || [],
      systemRequirements: {
        minimum: {
          os: game.systemRequirements?.minimum?.os || '',
          storage: game.systemRequirements?.minimum?.storage || '',
          processor: game.systemRequirements?.minimum?.processor || '',
          memory: game.systemRequirements?.minimum?.memory || '',
          graphics: game.systemRequirements?.minimum?.graphics || '',
          directX: game.systemRequirements?.minimum?.directX || ''
        },
        recommended: {
          os: game.systemRequirements?.recommended?.os || '',
          storage: game.systemRequirements?.recommended?.storage || '',
          processor: game.systemRequirements?.recommended?.processor || '',
          memory: game.systemRequirements?.recommended?.memory || '',
          graphics: game.systemRequirements?.recommended?.graphics || '',
          directX: game.systemRequirements?.recommended?.directX || ''
        }
      }
    });
    
    // Set media previews from existing game media
    if (game.media && game.media.length > 0) {
      const existingPreviews = game.media.map(url => ({
        url: url,
        type: url.match(/\.(mp4|webm|ogg)$/i) ? 'video' : 'image'
      }));
      setMediaPreviews(existingPreviews);
    }
    
    setEditingGameId(game._id);
    setShowGameForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteGame = async (gameId) => {
    if (window.confirm('Are you sure you want to delete this game? This will require admin approval.')) {
      try {
        await axios.delete(`/api/indie-games/${gameId}`);
        setMessage({ type: 'success', text: 'Delete request sent for approval!' });
        fetchMyGames();
      } catch (err) {
        setMessage({ type: 'error', text: 'Failed to delete game' });
      }
    }
  };

  return (
    <div className="container">
      <h2 style={{ color: 'white', marginBottom: '20px' }}>Dashboard - Welcome, {user?.username}!</h2>

      {/* Daily Check-In Widget */}
      <DailyCheckIn />

      {/* Daily Quests Widget */}
      <DailyQuests />

      {message.text && (
        <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
          {message.text}
        </div>
      )}

      <div style={{ 
        marginBottom: '20px',
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setActiveTab('community')}
          style={{
            padding: '14px 28px',
            fontSize: '15px',
            fontWeight: '600',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            background: activeTab === 'community' 
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'var(--card-bg-secondary)',
            color: activeTab === 'community' ? '#ffffff' : 'var(--text-primary)',
            boxShadow: activeTab === 'community' 
              ? '0 4px 15px rgba(102, 126, 234, 0.4)'
              : '0 2px 8px rgba(0, 0, 0, 0.1)',
            minWidth: '160px',
            textAlign: 'center'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== 'community') {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'community') {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
            }
          }}
        >
          🃏 Community Card
        </button>
        {isIndieDev && (
          <button
            onClick={() => setActiveTab('games')}
            style={{
              padding: '14px 28px',
              fontSize: '15px',
              fontWeight: '600',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              background: activeTab === 'games' 
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'var(--card-bg-secondary)',
              color: activeTab === 'games' ? '#ffffff' : 'var(--text-primary)',
              boxShadow: activeTab === 'games' 
                ? '0 4px 15px rgba(102, 126, 234, 0.4)'
                : '0 2px 8px rgba(0, 0, 0, 0.1)',
              minWidth: '160px',
              textAlign: 'center'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'games') {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'games') {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            🎮 My Games
          </button>
        )}
        {isIndieDev && (
          <button
            onClick={() => setActiveTab('analytics')}
            style={{
              padding: '14px 28px',
              fontSize: '15px',
              fontWeight: '600',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              background: activeTab === 'analytics' 
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'var(--card-bg-secondary)',
              color: activeTab === 'analytics' ? '#ffffff' : 'var(--text-primary)',
              boxShadow: activeTab === 'analytics' 
                ? '0 4px 15px rgba(102, 126, 234, 0.4)'
                : '0 2px 8px rgba(0, 0, 0, 0.1)',
              minWidth: '160px',
              textAlign: 'center'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'analytics') {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'analytics') {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            📊 Analytics
          </button>
        )}
        <button
          onClick={() => setActiveTab('reviews')}
          style={{
            padding: '14px 28px',
            fontSize: '15px',
            fontWeight: '600',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            background: activeTab === 'reviews' 
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'var(--card-bg-secondary)',
            color: activeTab === 'reviews' ? '#ffffff' : 'var(--text-primary)',
            boxShadow: activeTab === 'reviews' 
              ? '0 4px 15px rgba(102, 126, 234, 0.4)'
              : '0 2px 8px rgba(0, 0, 0, 0.1)',
            minWidth: '160px',
            textAlign: 'center'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== 'reviews') {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'reviews') {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
            }
          }}
        >
          📝 My Reviews
        </button>
        <button
          onClick={() => setActiveTab('giveaways')}
          style={{
            padding: '14px 28px',
            fontSize: '15px',
            fontWeight: '600',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            background: activeTab === 'giveaways' 
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'var(--card-bg-secondary)',
            color: activeTab === 'giveaways' ? '#ffffff' : 'var(--text-primary)',
            boxShadow: activeTab === 'giveaways' 
              ? '0 4px 15px rgba(102, 126, 234, 0.4)'
              : '0 2px 8px rgba(0, 0, 0, 0.1)',
            minWidth: '160px',
            textAlign: 'center'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== 'giveaways') {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'giveaways') {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
            }
          }}
        >
          🎁 My Giveaways
        </button>
      </div>

      {/* Community Card Tab */}
      {activeTab === 'community' && (
        <div>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>My Community Card</h3>
              {myCards.length === 0 && !showCardForm && (
                <button 
                  onClick={() => setShowCardForm(true)}
                  style={{
                    padding: '12px 24px',
                    fontSize: '15px',
                    fontWeight: '600',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#ffffff',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                    minWidth: '140px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                  }}
                >
                  Create Card
                </button>
              )}
              {myCards.length > 0 && !showCardForm && (
                <button 
                  onClick={() => editCard(myCards[0])}
                  style={{
                    padding: '12px 24px',
                    fontSize: '15px',
                    fontWeight: '600',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#ffffff',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                    minWidth: '140px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                  }}
                >
                  Edit Card
                </button>
              )}
            </div>

            {showCardForm ? (
              <div className="card" style={{ animation: 'fadeInUp 0.5s ease-out' }}>
                <h3 style={{
                  color: 'var(--text-accent)',
                  marginBottom: '24px',
                  fontSize: '22px',
                  fontWeight: '700'
                }}>
                  {myCards.length > 0 ? 'Edit Community Card' : 'Create Community Card'}
                </h3>

                <form onSubmit={handleCardSubmit}>
                  <div className="form-group">
                    <label style={{
                      display: 'block',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={cardFormData.name}
                      onChange={handleCardChange}
                      required
                      maxLength="100"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid var(--border-color)',
                        borderRadius: '8px',
                        background: 'var(--card-bg)',
                        color: 'var(--text-primary)',
                        fontSize: '15px',
                        transition: 'all 0.3s ease',
                        outline: 'none'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--btn-primary)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--border-color)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{
                      display: 'block',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      About *
                    </label>
                    <textarea
                      name="about"
                      value={cardFormData.about}
                      onChange={handleCardChange}
                      required
                      maxLength="1000"
                      rows="4"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid var(--border-color)',
                        borderRadius: '8px',
                        background: 'var(--card-bg)',
                        color: 'var(--text-primary)',
                        fontSize: '15px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        transition: 'all 0.3s ease',
                        outline: 'none'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--btn-primary)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--border-color)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Hashtags Input */}
                  <HashtagInput
                    hashtags={cardFormData.hashtags}
                    onChange={(newHashtags) => setCardFormData({ ...cardFormData, hashtags: newHashtags })}
                  />

                  <div className="form-group">
                    <label style={{
                      display: 'block',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Avatar Image
                    </label>
                    {avatarPreview && (
                      <div style={{
                        marginBottom: '12px',
                        display: 'flex',
                        justifyContent: 'center'
                      }}>
                        <img
                          src={avatarPreview}
                          alt="Avatar preview"
                          style={{
                            width: '100px',
                            height: '100px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '3px solid var(--btn-primary)'
                          }}
                        />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid var(--border-color)',
                        borderRadius: '8px',
                        background: 'var(--card-bg)',
                        color: 'var(--text-primary)',
                        fontSize: '15px',
                        transition: 'all 0.3s ease',
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      marginTop: '4px'
                    }}>
                      Maximum file size: 500KB. Recommended: Square image, at least 200x200px
                    </div>
                  </div>

                  <div className="form-group">
                    <label style={{
                      display: 'block',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Linktree URL
                    </label>
                    <input
                      type="url"
                      name="linktree"
                      value={cardFormData.linktree}
                      onChange={handleCardChange}
                      placeholder="https://linktr.ee/yourusername"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid var(--border-color)',
                        borderRadius: '8px',
                        background: 'var(--card-bg)',
                        color: 'var(--text-primary)',
                        fontSize: '15px',
                        transition: 'all 0.3s ease',
                        outline: 'none'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--btn-primary)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--border-color)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{
                      display: 'block',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Social Links
                    </label>
                    {cardFormData.socialLinks.map((link, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '8px',
                        padding: '12px',
                        background: 'var(--card-bg-secondary)',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)'
                      }}>
                        <span style={{
                          flex: 1,
                          color: 'var(--text-primary)',
                          fontSize: '14px'
                        }}>
                          <strong style={{ color: 'var(--text-accent)' }}>{link.platform}</strong>: {link.url}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeSocialLink(index)}
                          style={{
                            padding: '6px 12px',
                            background: 'linear-gradient(135deg, var(--btn-danger), var(--btn-danger-hover))',
                            color: 'var(--text-light)',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 2px 4px rgba(231, 76, 60, 0.3)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 8px rgba(231, 76, 60, 0.5)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px rgba(231, 76, 60, 0.3)';
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginTop: '12px',
                      flexWrap: 'wrap'
                    }}>
                      <input
                        type="text"
                        placeholder="Platform (e.g., Twitter)"
                        value={newSocialLink.platform}
                        onChange={(e) => setNewSocialLink({ ...newSocialLink, platform: e.target.value })}
                        style={{
                          flex: 1,
                          minWidth: '150px',
                          padding: '10px 12px',
                          border: '2px solid var(--border-color)',
                          borderRadius: '6px',
                          background: 'var(--card-bg)',
                          color: 'var(--text-primary)',
                          fontSize: '14px',
                          transition: 'all 0.3s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--btn-primary)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--border-color)';
                        }}
                      />
                      <input
                        type="url"
                        placeholder="URL"
                        value={newSocialLink.url}
                        onChange={(e) => setNewSocialLink({ ...newSocialLink, url: e.target.value })}
                        style={{
                          flex: 1,
                          minWidth: '200px',
                          padding: '10px 12px',
                          border: '2px solid var(--border-color)',
                          borderRadius: '6px',
                          background: 'var(--card-bg)',
                          color: 'var(--text-primary)',
                          fontSize: '14px',
                          transition: 'all 0.3s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--btn-primary)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--border-color)';
                        }}
                      />
                      <button
                        type="button"
                        onClick={addSocialLink}
                        style={{
                          padding: '10px 16px',
                          background: 'linear-gradient(135deg, var(--btn-secondary), var(--btn-secondary-hover))',
                          color: 'var(--text-light)',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 4px rgba(118, 75, 162, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 8px rgba(118, 75, 162, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 4px rgba(118, 75, 162, 0.3)';
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label style={{
                      display: 'block',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Donation Links
                    </label>
                    {cardFormData.donationLinks.map((link, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '8px',
                        padding: '12px',
                        background: 'var(--card-bg-secondary)',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)'
                      }}>
                        <span style={{
                          flex: 1,
                          color: 'var(--text-primary)',
                          fontSize: '14px'
                        }}>
                          <strong style={{ color: 'var(--text-accent)' }}>{link.platform}</strong>: {link.url}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeDonationLink(index)}
                          style={{
                            padding: '6px 12px',
                            background: 'linear-gradient(135deg, var(--btn-danger), var(--btn-danger-hover))',
                            color: 'var(--text-light)',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 2px 4px rgba(231, 76, 60, 0.3)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 8px rgba(231, 76, 60, 0.5)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px rgba(231, 76, 60, 0.3)';
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginTop: '12px',
                      flexWrap: 'wrap'
                    }}>
                      <input
                        type="text"
                        placeholder="Platform (e.g., PayPal)"
                        value={newDonationLink.platform}
                        onChange={(e) => setNewDonationLink({ ...newDonationLink, platform: e.target.value })}
                        style={{
                          flex: 1,
                          minWidth: '150px',
                          padding: '10px 12px',
                          border: '2px solid var(--border-color)',
                          borderRadius: '6px',
                          background: 'var(--card-bg)',
                          color: 'var(--text-primary)',
                          fontSize: '14px',
                          transition: 'all 0.3s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--btn-primary)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--border-color)';
                        }}
                      />
                      <input
                        type="url"
                        placeholder="URL"
                        value={newDonationLink.url}
                        onChange={(e) => setNewDonationLink({ ...newDonationLink, url: e.target.value })}
                        style={{
                          flex: 1,
                          minWidth: '200px',
                          padding: '10px 12px',
                          border: '2px solid var(--border-color)',
                          borderRadius: '6px',
                          background: 'var(--card-bg)',
                          color: 'var(--text-primary)',
                          fontSize: '14px',
                          transition: 'all 0.3s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--btn-primary)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--border-color)';
                        }}
                      />
                      <button
                        type="button"
                        onClick={addDonationLink}
                        style={{
                          padding: '10px 16px',
                          background: 'linear-gradient(135deg, var(--btn-secondary), var(--btn-secondary-hover))',
                          color: 'var(--text-light)',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 4px rgba(118, 75, 162, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 8px rgba(118, 75, 162, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 4px rgba(118, 75, 162, 0.3)';
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    marginTop: '24px',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        padding: '12px 24px',
                        background: loading ? 'var(--btn-disabled)' : 'linear-gradient(135deg, var(--btn-success), var(--btn-success-hover))',
                        color: 'var(--text-light)',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: loading ? 'none' : '0 4px 12px rgba(39, 174, 96, 0.3)',
                        opacity: loading ? 0.7 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!loading) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 6px 16px rgba(39, 174, 96, 0.5)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loading) {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 12px rgba(39, 174, 96, 0.3)';
                        }
                      }}
                    >
                      {loading ? 'Saving...' : 'Save Card'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCardForm(false);
                        resetCardForm();
                      }}
                      style={{
                        padding: '12px 24px',
                        background: 'var(--card-bg-secondary)',
                        color: 'var(--text-primary)',
                        border: '2px solid var(--border-color)',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = 'var(--btn-primary)';
                        e.target.style.background = 'var(--btn-primary)';
                        e.target.style.color = 'var(--text-light)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = 'var(--border-color)';
                        e.target.style.background = 'var(--card-bg-secondary)';
                        e.target.style.color = 'var(--text-primary)';
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            ) : myCards.length > 0 ? (
              <div>
                <h4>{myCards[0].name}</h4>
                <p><strong>Status:</strong> {myCards[0].isApproved ? '✅ Approved' : '⏳ Pending Approval'}</p>
                <p>{myCards[0].about}</p>
                {myCards[0].socialLinks.length > 0 && (
                  <p><strong>Social Links:</strong> {myCards[0].socialLinks.length}</p>
                )}
                {myCards[0].donationLinks.length > 0 && (
                  <p><strong>Donation Links:</strong> {myCards[0].donationLinks.length}</p>
                )}
                <button 
                  onClick={() => deleteCard(myCards[0]._id)}
                  style={{
                    marginTop: '10px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: '#ffffff',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                    minWidth: '140px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 18px rgba(239, 68, 68, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                  }}
                >
                  Delete Card
                </button>
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>You haven't created a community card yet.</p>
            )}
          </div>
        </div>
      )}

      {/* Indie Games Tab */}
      {activeTab === 'games' && isIndieDev && (
        <div>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>My Games</h3>
              {!showGameForm && (
                <button 
                  onClick={() => setShowGameForm(true)}
                  style={{
                    padding: '12px 24px',
                    fontSize: '15px',
                    fontWeight: '600',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#ffffff',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                    minWidth: '140px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                  }}
                >
                  Add Game
                </button>
              )}
            </div>

            {showGameForm && (
              <div className="card" style={{ animation: 'fadeInUp 0.5s ease-out', marginBottom: '30px' }}>
                <h3 style={{
                  color: 'var(--text-accent)',
                  marginBottom: '24px',
                  fontSize: '22px',
                  fontWeight: '700'
                }}>
                  {editingGameId ? 'Edit Indie Game' : 'Add Indie Game'}
                </h3>

                <form onSubmit={handleGameSubmit}>
                  <div className="form-group">
                    <label style={{
                      display: 'block',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Game Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={gameFormData.name}
                      onChange={handleGameChange}
                      required
                      maxLength="150"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid var(--border-color)',
                        borderRadius: '8px',
                        background: 'var(--card-bg)',
                        color: 'var(--text-primary)',
                        fontSize: '15px',
                        transition: 'all 0.3s ease',
                        outline: 'none'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--btn-primary)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--border-color)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{
                      display: 'block',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Description *
                    </label>
                    <textarea
                      name="description"
                      value={gameFormData.description}
                      onChange={handleGameChange}
                      required
                      maxLength="2000"
                      rows="4"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid var(--border-color)',
                        borderRadius: '8px',
                        background: 'var(--card-bg)',
                        color: 'var(--text-primary)',
                        fontSize: '15px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        transition: 'all 0.3s ease',
                        outline: 'none'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--btn-primary)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--border-color)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{
                      display: 'block',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Price *
                    </label>
                    <input
                      type="text"
                      name="price"
                      value={gameFormData.price}
                      onChange={handleGameChange}
                      required
                      maxLength="50"
                      placeholder="e.g., Free, $9.99, Pay What You Want"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid var(--border-color)',
                        borderRadius: '8px',
                        background: 'var(--card-bg)',
                        color: 'var(--text-primary)',
                        fontSize: '15px',
                        transition: 'all 0.3s ease',
                        outline: 'none'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--btn-primary)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--border-color)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Hashtags Input */}
                  <HashtagInput
                    hashtags={gameFormData.hashtags}
                    onChange={(newHashtags) => setGameFormData({ ...gameFormData, hashtags: newHashtags })}
                  />

                  {/* Version and Release Status */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{
                        display: 'block',
                        color: 'var(--text-primary)',
                        fontSize: '14px',
                        fontWeight: '600',
                        marginBottom: '8px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Version
                      </label>
                      <input
                        type="text"
                        name="version"
                        value={gameFormData.version}
                        onChange={handleGameChange}
                        placeholder="e.g., 1.0, v2.5.1"
                        maxLength="50"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid var(--border-color)',
                          borderRadius: '8px',
                          background: 'var(--card-bg)',
                          color: 'var(--text-primary)',
                          fontSize: '15px',
                          transition: 'all 0.3s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--btn-primary)';
                          e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--border-color)';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{
                        display: 'block',
                        color: 'var(--text-primary)',
                        fontSize: '14px',
                        fontWeight: '600',
                        marginBottom: '8px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Release Status
                      </label>
                      <select
                        name="releaseStatus"
                        value={gameFormData.releaseStatus}
                        onChange={handleGameChange}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid var(--border-color)',
                          borderRadius: '8px',
                          background: 'var(--card-bg)',
                          color: 'var(--text-primary)',
                          fontSize: '15px',
                          transition: 'all 0.3s ease',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--btn-primary)';
                          e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--border-color)';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        <option value="">Select status...</option>
                        <option value="Coming Soon">Coming Soon</option>
                        <option value="Early Access">Early Access</option>
                        <option value="Released">Released</option>
                      </select>
                    </div>
                  </div>

                  {/* Maturity Rating */}
                  <div className="form-group">
                    <label style={{
                      display: 'block',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Maturity Rating
                    </label>
                    <input
                      type="text"
                      name="maturityRating"
                      value={gameFormData.maturityRating}
                      onChange={handleGameChange}
                      placeholder="e.g., MA15+, E for Everyone, Teen"
                      maxLength="50"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid var(--border-color)',
                        borderRadius: '8px',
                        background: 'var(--card-bg)',
                        color: 'var(--text-primary)',
                        fontSize: '15px',
                        transition: 'all 0.3s ease',
                        outline: 'none'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--btn-primary)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--border-color)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Genres */}
                  <div className="form-group">
                    <label style={{
                      display: 'block',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Genres {gameFormData.genre.length > 0 && `(${gameFormData.genre.length}/10)`}
                    </label>
                    {gameFormData.genre.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                        {gameFormData.genre.map((genre, index) => (
                          <span key={index} style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 12px',
                            background: 'linear-gradient(135deg, var(--btn-secondary), var(--btn-secondary-hover))',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}>
                            {genre}
                            <button
                              type="button"
                              onClick={() => removeGenre(index)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '16px',
                                lineHeight: '1',
                                padding: 0
                              }}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={newGenre}
                        onChange={(e) => setNewGenre(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addGenre())}
                        placeholder="Add genre (e.g., Action, RPG, Puzzle)"
                        disabled={gameFormData.genre.length >= 10}
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          border: '2px solid var(--border-color)',
                          borderRadius: '6px',
                          background: gameFormData.genre.length >= 10 ? 'var(--card-bg-secondary)' : 'var(--card-bg)',
                          color: 'var(--text-primary)',
                          fontSize: '14px',
                          transition: 'all 0.3s ease',
                          outline: 'none',
                          opacity: gameFormData.genre.length >= 10 ? 0.6 : 1
                        }}
                      />
                      <button
                        type="button"
                        onClick={addGenre}
                        disabled={!newGenre.trim() || gameFormData.genre.length >= 10}
                        style={{
                          padding: '10px 16px',
                          background: (!newGenre.trim() || gameFormData.genre.length >= 10) ? 'var(--btn-disabled)' : 'linear-gradient(135deg, var(--btn-secondary), var(--btn-secondary-hover))',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: (!newGenre.trim() || gameFormData.genre.length >= 10) ? 'not-allowed' : 'pointer',
                          transition: 'all 0.3s ease',
                          opacity: (!newGenre.trim() || gameFormData.genre.length >= 10) ? 0.6 : 1
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="form-group">
                    <label style={{
                      display: 'block',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Features {gameFormData.features.length > 0 && `(${gameFormData.features.length}/20)`}
                    </label>
                    {gameFormData.features.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                        {gameFormData.features.map((feature, index) => (
                          <span key={index} style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 12px',
                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}>
                            {feature}
                            <button
                              type="button"
                              onClick={() => removeFeature(index)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '16px',
                                lineHeight: '1',
                                padding: 0
                              }}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={newFeature}
                        onChange={(e) => setNewFeature(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                        placeholder="Add feature (e.g., Single Player, Multiplayer, Controller Support)"
                        disabled={gameFormData.features.length >= 20}
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          border: '2px solid var(--border-color)',
                          borderRadius: '6px',
                          background: gameFormData.features.length >= 20 ? 'var(--card-bg-secondary)' : 'var(--card-bg)',
                          color: 'var(--text-primary)',
                          fontSize: '14px',
                          transition: 'all 0.3s ease',
                          outline: 'none',
                          opacity: gameFormData.features.length >= 20 ? 0.6 : 1
                        }}
                      />
                      <button
                        type="button"
                        onClick={addFeature}
                        disabled={!newFeature.trim() || gameFormData.features.length >= 20}
                        style={{
                          padding: '10px 16px',
                          background: (!newFeature.trim() || gameFormData.features.length >= 20) ? 'var(--btn-disabled)' : 'linear-gradient(135deg, #667eea, #764ba2)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: (!newFeature.trim() || gameFormData.features.length >= 20) ? 'not-allowed' : 'pointer',
                          transition: 'all 0.3s ease',
                          opacity: (!newFeature.trim() || gameFormData.features.length >= 20) ? 0.6 : 1
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* System Requirements */}
                  <div className="form-group">
                    <h4 style={{
                      color: 'var(--text-accent)',
                      fontSize: '18px',
                      fontWeight: '700',
                      marginBottom: '16px',
                      marginTop: '24px'
                    }}>
                      System Requirements
                    </h4>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                      {/* Minimum Requirements */}
                      <div>
                        <h5 style={{
                          color: 'var(--text-primary)',
                          fontSize: '16px',
                          fontWeight: '600',
                          marginBottom: '12px'
                        }}>
                          Minimum
                        </h5>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <input
                            type="text"
                            placeholder="OS Version (e.g., Windows 10)"
                            value={gameFormData.systemRequirements.minimum.os}
                            onChange={(e) => handleSystemReqChange('minimum', 'os', e.target.value)}
                            style={{
                              padding: '10px 12px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              background: 'var(--card-bg)',
                              color: 'var(--text-primary)',
                              fontSize: '14px'
                            }}
                          />
                          <input
                            type="text"
                            placeholder="Storage (e.g., 5GB Free)"
                            value={gameFormData.systemRequirements.minimum.storage}
                            onChange={(e) => handleSystemReqChange('minimum', 'storage', e.target.value)}
                            style={{
                              padding: '10px 12px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              background: 'var(--card-bg)',
                              color: 'var(--text-primary)',
                              fontSize: '14px'
                            }}
                          />
                          <input
                            type="text"
                            placeholder="DirectX (e.g., Version 11)"
                            value={gameFormData.systemRequirements.minimum.directX}
                            onChange={(e) => handleSystemReqChange('minimum', 'directX', e.target.value)}
                            style={{
                              padding: '10px 12px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              background: 'var(--card-bg)',
                              color: 'var(--text-primary)',
                              fontSize: '14px'
                            }}
                          />
                          <input
                            type="text"
                            placeholder="Memory (e.g., 8GB RAM)"
                            value={gameFormData.systemRequirements.minimum.memory}
                            onChange={(e) => handleSystemReqChange('minimum', 'memory', e.target.value)}
                            style={{
                              padding: '10px 12px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              background: 'var(--card-bg)',
                              color: 'var(--text-primary)',
                              fontSize: '14px'
                            }}
                          />
                          <input
                            type="text"
                            placeholder="Processor (e.g., Intel Core i5-4460)"
                            value={gameFormData.systemRequirements.minimum.processor}
                            onChange={(e) => handleSystemReqChange('minimum', 'processor', e.target.value)}
                            style={{
                              padding: '10px 12px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              background: 'var(--card-bg)',
                              color: 'var(--text-primary)',
                              fontSize: '14px'
                            }}
                          />
                          <input
                            type="text"
                            placeholder="Graphics (e.g., NVIDIA GTX 960)"
                            value={gameFormData.systemRequirements.minimum.graphics}
                            onChange={(e) => handleSystemReqChange('minimum', 'graphics', e.target.value)}
                            style={{
                              padding: '10px 12px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              background: 'var(--card-bg)',
                              color: 'var(--text-primary)',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                      </div>

                      {/* Recommended Requirements */}
                      <div>
                        <h5 style={{
                          color: 'var(--text-primary)',
                          fontSize: '16px',
                          fontWeight: '600',
                          marginBottom: '12px'
                        }}>
                          Recommended
                        </h5>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <input
                            type="text"
                            placeholder="OS Version (e.g., Windows 10)"
                            value={gameFormData.systemRequirements.recommended.os}
                            onChange={(e) => handleSystemReqChange('recommended', 'os', e.target.value)}
                            style={{
                              padding: '10px 12px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              background: 'var(--card-bg)',
                              color: 'var(--text-primary)',
                              fontSize: '14px'
                            }}
                          />
                          <input
                            type="text"
                            placeholder="Storage (e.g., 10GB Free)"
                            value={gameFormData.systemRequirements.recommended.storage}
                            onChange={(e) => handleSystemReqChange('recommended', 'storage', e.target.value)}
                            style={{
                              padding: '10px 12px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              background: 'var(--card-bg)',
                              color: 'var(--text-primary)',
                              fontSize: '14px'
                            }}
                          />
                          <input
                            type="text"
                            placeholder="DirectX (e.g., Version 12)"
                            value={gameFormData.systemRequirements.recommended.directX}
                            onChange={(e) => handleSystemReqChange('recommended', 'directX', e.target.value)}
                            style={{
                              padding: '10px 12px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              background: 'var(--card-bg)',
                              color: 'var(--text-primary)',
                              fontSize: '14px'
                            }}
                          />
                          <input
                            type="text"
                            placeholder="Memory (e.g., 16GB RAM)"
                            value={gameFormData.systemRequirements.recommended.memory}
                            onChange={(e) => handleSystemReqChange('recommended', 'memory', e.target.value)}
                            style={{
                              padding: '10px 12px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              background: 'var(--card-bg)',
                              color: 'var(--text-primary)',
                              fontSize: '14px'
                            }}
                          />
                          <input
                            type="text"
                            placeholder="Processor (e.g., Intel Core i7-8700K)"
                            value={gameFormData.systemRequirements.recommended.processor}
                            onChange={(e) => handleSystemReqChange('recommended', 'processor', e.target.value)}
                            style={{
                              padding: '10px 12px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              background: 'var(--card-bg)',
                              color: 'var(--text-primary)',
                              fontSize: '14px'
                            }}
                          />
                          <input
                            type="text"
                            placeholder="Graphics (e.g., NVIDIA GTX 1070)"
                            value={gameFormData.systemRequirements.recommended.graphics}
                            onChange={(e) => handleSystemReqChange('recommended', 'graphics', e.target.value)}
                            style={{
                              padding: '10px 12px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              background: 'var(--card-bg)',
                              color: 'var(--text-primary)',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label style={{
                      display: 'block',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Game Media (Images & Videos) {mediaPreviews.length > 0 && `- ${mediaPreviews.length}/10 files`}
                    </label>
                    {mediaPreviews.length > 0 && (
                      <div style={{
                        marginBottom: '12px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                        gap: '10px'
                      }}>
                        {mediaPreviews.map((preview, index) => (
                          <div key={index} style={{
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: '2px solid var(--border-color)',
                            position: 'relative'
                          }}>
                            {preview.type === 'image' ? (
                              <img
                                src={preview.url}
                                alt={`Preview ${index + 1}`}
                                style={{
                                  width: '100%',
                                  height: '120px',
                                  objectFit: 'cover'
                                }}
                              />
                            ) : (
                              <video
                                src={preview.url}
                                style={{
                                  width: '100%',
                                  height: '120px',
                                  objectFit: 'cover',
                                  background: 'var(--card-bg-secondary)'
                                }}
                              />
                            )}
                            {/* Remove button */}
                            <button
                              type="button"
                              onClick={() => removeMediaFile(index)}
                              style={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                background: 'rgba(239, 68, 68, 0.9)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                fontSize: '16px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = 'rgba(239, 68, 68, 1)';
                                e.target.style.transform = 'scale(1.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'rgba(239, 68, 68, 0.9)';
                                e.target.style.transform = 'scale(1)';
                              }}
                            >
                              ×
                            </button>
                            {/* Order badge */}
                            <div style={{
                              position: 'absolute',
                              bottom: '4px',
                              left: '4px',
                              background: 'rgba(0, 0, 0, 0.7)',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              {index === 0 ? '🌟 Cover' : `#${index + 1}`}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      onChange={handleMediaChange}
                      disabled={mediaFiles.length >= 10}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid var(--border-color)',
                        borderRadius: '8px',
                        background: mediaFiles.length >= 10 ? 'var(--card-bg-secondary)' : 'var(--card-bg)',
                        color: 'var(--text-primary)',
                        fontSize: '15px',
                        transition: 'all 0.3s ease',
                        outline: 'none',
                        cursor: mediaFiles.length >= 10 ? 'not-allowed' : 'pointer',
                        opacity: mediaFiles.length >= 10 ? 0.6 : 1
                      }}
                    />
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      marginTop: '4px'
                    }}>
                      {mediaFiles.length >= 10 
                        ? '✓ Maximum files reached (10/10)' 
                        : `Click to add more files. You can select multiple at once. ${10 - mediaFiles.length} slots remaining.`
                      }
                      <br />
                      💡 First file will be used as the cover image
                    </div>
                  </div>

                  <div className="form-group">
                    <label style={{
                      display: 'block',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Store Links * (at least one required)
                    </label>
                    {gameFormData.storeLinks.map((link, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '8px',
                        padding: '12px',
                        background: 'var(--card-bg-secondary)',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)'
                      }}>
                        <span style={{
                          flex: 1,
                          color: 'var(--text-primary)',
                          fontSize: '14px'
                        }}>
                          <strong style={{ color: 'var(--text-accent)' }}>{link.platform}</strong>: {link.url}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeStoreLink(index)}
                          style={{
                            padding: '6px 12px',
                            background: 'linear-gradient(135deg, var(--btn-danger), var(--btn-danger-hover))',
                            color: 'var(--text-light)',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 2px 4px rgba(231, 76, 60, 0.3)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 8px rgba(231, 76, 60, 0.5)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px rgba(231, 76, 60, 0.3)';
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginTop: '12px',
                      flexWrap: 'wrap'
                    }}>
                      <input
                        type="text"
                        placeholder="Platform (e.g., Steam, itch.io)"
                        value={newStoreLink.platform}
                        onChange={(e) => setNewStoreLink({ ...newStoreLink, platform: e.target.value })}
                        style={{
                          flex: 1,
                          minWidth: '150px',
                          padding: '10px 12px',
                          border: '2px solid var(--border-color)',
                          borderRadius: '6px',
                          background: 'var(--card-bg)',
                          color: 'var(--text-primary)',
                          fontSize: '14px',
                          transition: 'all 0.3s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--btn-primary)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--border-color)';
                        }}
                      />
                      <input
                        type="url"
                        placeholder="URL"
                        value={newStoreLink.url}
                        onChange={(e) => setNewStoreLink({ ...newStoreLink, url: e.target.value })}
                        style={{
                          flex: 1,
                          minWidth: '200px',
                          padding: '10px 12px',
                          border: '2px solid var(--border-color)',
                          borderRadius: '6px',
                          background: 'var(--card-bg)',
                          color: 'var(--text-primary)',
                          fontSize: '14px',
                          transition: 'all 0.3s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--btn-primary)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--border-color)';
                        }}
                      />
                      <button
                        type="button"
                        onClick={addStoreLink}
                        style={{
                          padding: '10px 16px',
                          background: 'linear-gradient(135deg, var(--btn-secondary), var(--btn-secondary-hover))',
                          color: 'var(--text-light)',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 4px rgba(118, 75, 162, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 8px rgba(118, 75, 162, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 4px rgba(118, 75, 162, 0.3)';
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    marginTop: '24px',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      type="submit"
                      disabled={loading || gameFormData.storeLinks.length === 0}
                      style={{
                        padding: '12px 24px',
                        background: (loading || gameFormData.storeLinks.length === 0) ? 'var(--btn-disabled)' : 'linear-gradient(135deg, var(--btn-success), var(--btn-success-hover))',
                        color: 'var(--text-light)',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: (loading || gameFormData.storeLinks.length === 0) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: (loading || gameFormData.storeLinks.length === 0) ? 'none' : '0 4px 12px rgba(39, 174, 96, 0.3)',
                        opacity: (loading || gameFormData.storeLinks.length === 0) ? 0.7 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!loading && gameFormData.storeLinks.length > 0) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 6px 16px rgba(39, 174, 96, 0.5)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loading && gameFormData.storeLinks.length > 0) {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 12px rgba(39, 174, 96, 0.3)';
                        }
                      }}
                    >
                      {loading ? 'Saving...' : (editingGameId ? 'Update Game' : 'Save Game')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowGameForm(false);
                        resetGameForm();
                      }}
                      style={{
                        padding: '12px 24px',
                        background: 'var(--card-bg-secondary)',
                        color: 'var(--text-primary)',
                        border: '2px solid var(--border-color)',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = 'var(--btn-primary)';
                        e.target.style.background = 'var(--btn-primary)';
                        e.target.style.color = 'var(--text-light)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = 'var(--border-color)';
                        e.target.style.background = 'var(--card-bg-secondary)';
                        e.target.style.color = 'var(--text-primary)';
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {myGames.length === 0 && !showGameForm ? (
              <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>You haven't added any games yet.</p>
            ) : (
              <div style={{ marginTop: '20px', display: 'grid', gap: '20px' }}>
                {myGames.map((game) => (
                  <div key={game._id} style={{ 
                    padding: '20px', 
                    background: 'var(--card-bg)', 
                    borderRadius: '12px', 
                    border: '1px solid var(--border-color)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 8px 0', color: 'var(--text-accent)', fontSize: '20px' }}>{game.name}</h4>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: game.isApproved ? 'var(--success-bg)' : 'var(--warning-bg)',
                            color: game.isApproved ? 'var(--success-text)' : 'var(--warning-text)'
                          }}>
                            {game.isApproved ? '✅ Approved' : '⏳ Pending Approval'}
                          </span>
                          {game.version && (
                            <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                              Version: {game.version}
                            </span>
                          )}
                          {game.releaseStatus && (
                            <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                              {game.releaseStatus}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>
                      <strong style={{ color: 'var(--text-primary)' }}>Price:</strong> {game.price}
                    </p>
                    
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                      {game.description.substring(0, 150)}...
                    </p>
                    
                    {game.genre && game.genre.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
                        {game.genre.map((g, idx) => (
                          <span key={idx} style={{
                            padding: '4px 10px',
                            background: 'var(--card-bg-secondary)',
                            borderRadius: '12px',
                            fontSize: '12px',
                            color: 'var(--text-primary)'
                          }}>
                            {g}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <button 
                        onClick={() => handleEditGame(game)} 
                        style={{
                          padding: '10px 20px',
                          background: 'linear-gradient(135deg, var(--btn-secondary), var(--btn-secondary-hover))',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                        }}
                      >
                        Edit Game
                      </button>
                      <button 
                        onClick={() => deleteGame(game._id)} 
                        style={{
                          padding: '10px 20px',
                          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.3)';
                        }}
                      >
                        Delete Game
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reviews Tab */}
      {activeTab === 'reviews' && (
        <div>
          <div className="card">
            <h3 style={{ marginBottom: '20px', color: 'var(--text-accent)' }}>My Reviews</h3>
            
            {myReviews.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>📝</div>
                <h4 style={{ color: 'var(--text-primary)', marginBottom: '12px' }}>No Reviews Yet</h4>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
                  You haven't written any reviews yet. Visit the Community or Indie Games pages to leave reviews!
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <a href="/community-cards" className="btn btn-primary" style={{ color: 'white' }}>Browse Community</a>
                  <a href="/indie-games" className="btn btn-secondary" style={{ color: 'white' }}>Browse Games</a>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                {myReviews.map((review) => (
                  <div key={review._id} style={{
                    padding: '20px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    background: 'var(--bg-card)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}>
                    {editingReviewId === review._id ? (
                      <ReviewForm
                        existingReview={review}
                        onSuccess={() => {
                          setEditingReviewId(null);
                          fetchMyReviews();
                          setMessage({ type: 'success', text: 'Review updated successfully. It will be visible after admin approval.' });
                        }}
                        onCancel={() => setEditingReviewId(null)}
                      />
                    ) : (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                          <div>
                            <h4 style={{ margin: '0 0 4px 0', color: 'var(--text-accent)' }}>
                              {review.targetType === 'CommunityCard' ? 'Community Card' : 'Indie Game'}: {review.targetId?.name || 'Unknown Item'}
                            </h4>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div className="star-rating-display">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <span
                                    key={star}
                                    className={`star ${star <= review.rating ? 'filled' : 'empty'}`}
                                  >
                                    ★
                                  </span>
                                ))}
                              </div>
                              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                                {new Date(review.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: review.status === 'approved' ? 'var(--success-bg)' : 
                                       review.status === 'pending' ? 'var(--warning-bg)' : 'var(--error-bg)',
                            color: review.status === 'approved' ? 'var(--success-text)' : 
                                  review.status === 'pending' ? 'var(--warning-text)' : 'var(--error-text)'
                          }}>
                            {review.status === 'approved' ? '✅ Approved' : 
                             review.status === 'pending' ? '⏳ Pending' : '❌ Rejected'}
                          </span>
                        </div>
                        
                        {review.comment && (
                          <p style={{ 
                            margin: '12px 0', 
                            color: 'var(--text-primary)',
                            lineHeight: '1.5',
                            fontStyle: review.status !== 'approved' ? 'italic' : 'normal',
                            opacity: review.status !== 'approved' ? 0.7 : 1
                          }}>
                            {review.comment}
                          </p>
                        )}
                        
                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                          <button
                            onClick={() => setEditingReviewId(review._id)}
                            className="btn btn-primary"
                            style={{ fontSize: '12px', padding: '6px 12px' }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteReview(review._id)}
                            className="btn btn-danger"
                            style={{ fontSize: '12px', padding: '6px 12px' }}
                          >
                            Delete
                          </button>
                          {review.targetType === 'CommunityCard' && (
                            <a 
                              href={`/community`}
                              className="btn btn-secondary"
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                            >
                              View Card
                            </a>
                          )}
                          {review.targetType === 'IndieGame' && (
                            <a 
                              href={`/indie-games`}
                              className="btn btn-secondary"
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                            >
                              View Game
                            </a>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* My Giveaways Tab */}
      {activeTab === 'giveaways' && (
        <div>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>My Giveaways</h3>
              <a 
                href="/giveaways"
                style={{
                  padding: '12px 24px',
                  fontSize: '15px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#ffffff',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  minWidth: '180px',
                  textDecoration: 'none',
                  display: 'inline-block',
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                }}
              >
                Create New Giveaway
              </a>
            </div>

            {myGiveaways.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px' }}>
                You haven't created any giveaways yet.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {myGiveaways.map((giveaway) => (
                  <div
                    key={giveaway._id}
                    style={{
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '15px',
                      backgroundColor: 'var(--card-bg)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                          <h4 style={{ margin: 0, color: 'var(--text-primary)' }}>{giveaway.title}</h4>
                          <span
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              backgroundColor:
                                giveaway.status === 'ongoing' ? '#4CAF50' :
                                giveaway.status === 'closed' ? '#FF9800' :
                                giveaway.status === 'rolled' ? '#9C27B0' : '#757575',
                              color: 'white'
                            }}
                          >
                            {giveaway.status === 'ongoing' ? '🔥 Live' :
                             giveaway.status === 'closed' ? '🔒 Closed' :
                             giveaway.status === 'rolled' ? '🏆 Rolled' : '⏱️ Ended'}
                          </span>
                        </div>
                        <p style={{ margin: '5px 0', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                          🎁 Prize: {giveaway.prize}
                        </p>
                        <p style={{ margin: '5px 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                          👥 {giveaway.participantCount} participants
                        </p>
                        <p style={{ margin: '5px 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                          📅 Ends: {new Date(giveaway.endDate).toLocaleDateString()}
                        </p>
                        {(giveaway.winners?.length > 0 || giveaway.currentWinner) && (
                          <p style={{ margin: '5px 0', fontSize: '0.85rem', color: 'var(--success-text)', fontWeight: 'bold' }}>
                            🏆 {giveaway.winners?.length > 1 ? `Winners: ${giveaway.winners.map(w => w.username).join(', ')}` : `Winner: ${giveaway.winners?.[0]?.username || giveaway.currentWinner?.username}`}
                          </p>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleDeleteGiveaway(giveaway._id);
                          }}
                          className="btn btn-danger"
                          style={{ fontSize: '14px', padding: '8px 16px' }}
                        >
                          Delete
                        </button>
                        <a
                          href="/giveaways"
                          className="btn btn-primary"
                          style={{ fontSize: '14px', padding: '8px 16px', color: 'white' }}
                        >
                          View Details
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {showVerificationModal && verificationStatus && !verificationStatus.allVerified && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(8px)',
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: '20px',
            padding: '40px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            border: '2px solid rgba(239, 68, 68, 0.3)',
            animation: 'slideInUp 0.4s ease-out'
          }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '30px'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: '40px',
                boxShadow: '0 10px 25px rgba(239, 68, 68, 0.3)'
              }}>
                ⚠️
              </div>
              <h2 style={{
                color: 'var(--text-primary)',
                fontSize: '28px',
                fontWeight: '800',
                marginBottom: '10px'
              }}>
                Verification Required
              </h2>
              <p style={{
                color: 'var(--text-secondary)',
                fontSize: '16px',
                lineHeight: '1.6'
              }}>
                You need to verify all your social accounts before you can create or maintain a community card.
              </p>
            </div>

            <div style={{
              background: 'var(--card-bg)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '25px'
            }}>
              <h3 style={{
                color: 'var(--text-primary)',
                fontSize: '16px',
                fontWeight: '700',
                marginBottom: '15px',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Account Status:
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: verificationStatus.steam ? 'rgba(76, 175, 80, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  borderRadius: '8px',
                  border: `1px solid ${verificationStatus.steam ? 'rgba(76, 175, 80, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                }}>
                  <span style={{ fontSize: '24px' }}>🎮</span>
                  <span style={{ color: 'var(--text-primary)', flex: 1, fontWeight: '600' }}>Steam</span>
                  {verificationStatus.steam ? (
                    <span style={{ color: 'var(--success-text)', fontWeight: 'bold', fontSize: '18px' }}>✓</span>
                  ) : (
                    <span style={{ color: 'var(--error-text)', fontWeight: 'bold', fontSize: '18px' }}>✗</span>
                  )}
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: verificationStatus.discord ? 'rgba(76, 175, 80, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  borderRadius: '8px',
                  border: `1px solid ${verificationStatus.discord ? 'rgba(76, 175, 80, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                }}>
                  <span style={{ fontSize: '24px' }}>💬</span>
                  <span style={{ color: 'var(--text-primary)', flex: 1, fontWeight: '600' }}>Discord</span>
                  {verificationStatus.discord ? (
                    <span style={{ color: 'var(--success-text)', fontWeight: 'bold', fontSize: '18px' }}>✓</span>
                  ) : (
                    <span style={{ color: 'var(--error-text)', fontWeight: 'bold', fontSize: '18px' }}>✗</span>
                  )}
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: verificationStatus.twitter ? 'rgba(76, 175, 80, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  borderRadius: '8px',
                  border: `1px solid ${verificationStatus.twitter ? 'rgba(76, 175, 80, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                }}>
                  <span style={{ fontSize: '24px' }}>🐦</span>
                  <span style={{ color: 'var(--text-primary)', flex: 1, fontWeight: '600' }}>Twitter</span>
                  {verificationStatus.twitter ? (
                    <span style={{ color: 'var(--success-text)', fontWeight: 'bold', fontSize: '18px' }}>✓</span>
                  ) : (
                    <span style={{ color: 'var(--error-text)', fontWeight: 'bold', fontSize: '18px' }}>✗</span>
                  )}
                </div>
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => setShowVerificationModal(false)}
                style={{
                  padding: '14px 28px',
                  background: 'var(--card-bg)',
                  color: 'var(--text-secondary)',
                  border: '2px solid var(--border-color)',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--hover-bg)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--card-bg)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Close
              </button>
              <a
                href="/settings"
                style={{
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  display: 'inline-block',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(239, 68, 68, 0.3)';
                }}
              >
                Verify Accounts →
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && isIndieDev && (
        <DeveloperAnalytics />
      )}
    </div>
  );
};

export default Dashboard;
